import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiPlus } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { AssetTypePicker } from '../asset-type-picker/AssetTypePicker'
import { useCan } from '../../contexts/PermissionsContext'
import { useCreateComplianceRule, type ComplianceRule } from '../../hooks/useComplianceApi'

/**
 * One rule family on the Règles de gestion page (spec 2026-10-27-18): the
 * family's label, then one row per member (the default first, then its
 * per-type overrides), and "Set a threshold for a type" which creates an
 * override as a copy of the default member.
 */
export function RuleFamilyBlock({
    org,
    family,
    members,
    children,
}: {
    org: string
    family: string
    members: ComplianceRule[]
    children: ReactNode
}) {
    const { t, i18n } = useTranslation('compliance')
    // POST /compliance/rules is gated by the compliance resource.
    const canCreate = useCan('compliance.write')
    const [open, setOpen] = useState(false)
    const def = members[0]

    return (
        <div data-testid={`rule-family-${family}`}>
            <div className="flex items-center gap-2 bg-muted/30 px-4 py-2">
                <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-foreground">
                        {t(`business_rules.family.${family}`, {
                            // A family founded by "Dupliquer et adapter" is named
                            // after its rule's code: show the rule's label.
                            defaultValue: def?.label?.[i18n.language] || def?.label?.fr || def?.label?.en || family,
                        })}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        {t('business_rules.family_hint', {
                            defaultValue: 'Le seuil du type le plus précis s’applique ; un type désactivé n’est plus surveillé.',
                        })}
                    </div>
                </div>
                {canCreate && def && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOpen(true)}
                        data-testid={`rule-family-add-${family}`}
                    >
                        <AppIcon path={mdiPlus} size="sm" />
                        {t('business_rules.family_add', { defaultValue: 'Seuil pour un type' })}
                    </Button>
                )}
            </div>
            <div className="divide-y">{children}</div>
            {open && def && (
                <AddFamilyTypeDialog
                    org={org}
                    family={family}
                    def={def}
                    members={members}
                    onClose={() => setOpen(false)}
                />
            )}
        </div>
    )
}

function AddFamilyTypeDialog({
    org,
    family,
    def,
    members,
    onClose,
}: {
    org: string
    family: string
    def: ComplianceRule
    members: ComplianceRule[]
    onClose: () => void
}) {
    const { t } = useTranslation('compliance')
    const [typeUid, setTypeUid] = useState('')
    const [error, setError] = useState<string | null>(null)
    const create = useCreateComplianceRule(org)
    const taken = members.some((m) => m.asset_type_uid === typeUid && (m.jurisdiction || '*') === (def.jurisdiction || '*'))

    const submit = () => {
        if (!typeUid || taken) return
        setError(null)
        create.mutate(
            {
                family,
                asset_type_uid: typeUid,
                compliance_control_uid: def.compliance_control_uid,
                rule_class: def.rule_class,
                jurisdiction: def.jurisdiction,
                label: def.label,
                description: def.description,
                conditions: def.conditions,
                check: def.check,
                severity: def.severity,
                frequency_months: def.frequency_months,
                periodicity: def.periodicity,
                auto_create_issue: def.auto_create_issue,
                enabled: true,
            },
            {
                onSuccess: onClose,
                onError: (e: unknown) => {
                    const code = (e as { code?: string; error?: { code?: string } })?.error?.code ?? (e as { code?: string })?.code
                    setError(code === 'family_member_exists'
                        ? t('business_rules.family_add_exists', { defaultValue: 'Ce type a déjà son propre seuil.' })
                        : t('business_rules.family_add_error', { defaultValue: 'Impossible de créer ce seuil.' }))
                },
            },
        )
    }

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
            <DialogContent data-testid={`rule-family-add-dialog-${family}`}>
                <DialogHeader>
                    <DialogTitle>{t('business_rules.family_add_title', { defaultValue: 'Seuil propre à un type d’actif' })}</DialogTitle>
                    <DialogDescription>
                        {t('business_rules.family_add_description', {
                            defaultValue: 'Le nouveau seuil part d’une copie de la règle générale ; ajustez-le ensuite sur sa ligne.',
                        })}
                    </DialogDescription>
                </DialogHeader>
                <AssetTypePicker
                    org={org}
                    value={typeUid}
                    onChange={setTypeUid}
                    restrictToDescendantsOf={def.asset_type_uid || undefined}
                    placeholder={t('business_rules.family_add_pick', { defaultValue: 'Choisir un type' })}
                    triggerTestId="rule-family-type-picker"
                />
                {(taken || error) && (
                    <p className="text-sm text-destructive" data-testid="rule-family-add-error">
                        {taken ? t('business_rules.family_add_exists', { defaultValue: 'Ce type a déjà son propre seuil.' }) : error}
                    </p>
                )}
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>
                        {t('business_rules.cancel', { defaultValue: 'Annuler' })}
                    </Button>
                    <Button onClick={submit} disabled={!typeUid || taken || create.isPending} data-testid="rule-family-add-submit">
                        {t('business_rules.family_add_submit', { defaultValue: 'Créer le seuil' })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
